import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ScheduleService } from '../api/ScheduleAPI';
import { ShiftRequestsService } from '../api/ShiftRequestsAPI';
import { StaffService } from '../api/StaffAPI';
import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { LOCATION_META, CITIES } from '../constants';
import { Header } from '../components/Header/Header';
import s from '../styles/staff.module.css';

const DAY_MS = 86400000;
const iso = (dt) => new Date(dt).toISOString().slice(0, 10);
const utcDay = (key) => new Date(`${key}T00:00:00Z`);
const addDays = (key, n) => iso(new Date(utcDay(key).getTime() + n * DAY_MS));
const localDay = (v) => {
  const dt = new Date(v);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const weekStartOf = (key) => {
  const dt = utcDay(key);
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  return iso(dt);
};

const dur = (mins) => {
  const n = Math.max(0, Math.round(mins || 0));
  if (!n) return '';
  const h = Math.floor(n / 60);
  return h ? `${h}h${n % 60 ? ` ${n % 60}m` : ''}` : `${n}m`;
};
const tm = (v) => (v ? new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—');
const dShort = (key) => utcDay(key).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dowOf = (key) => utcDay(key).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
const fullName = (m) => m.name || `${m.given_name || ''} ${m.family_name || ''}`.trim() || m.externalId;
const initials = (n) => String(n || '').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

// Minutes from midnight → a "HH:MM" the time inputs understand, and back.
const hhmm = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
const minsOf = (v) => {
  const [h, m] = String(v || '0:0').split(':').map(Number);
  return h * 60 + m;
};
const label12 = (mins) => new Date(Date.UTC(2020, 0, 1, Math.floor(mins / 60), mins % 60))
  .toLocaleTimeString('en-US', { hour: 'numeric', minute: mins % 60 ? '2-digit' : undefined, timeZone: 'UTC' });

// The three shifts this shop actually runs.
const PRESETS = [
  { id: 'open',  label: 'Open',  from: 7 * 60,  to: 15 * 60 },
  { id: 'mid',   label: 'Mid',   from: 9 * 60,  to: 17 * 60 },
  { id: 'close', label: 'Close', from: 12 * 60, to: 20 * 60 },
];

const REQ_LABEL = {
  time_off: 'Time off', day_unavailable: 'Unavailable',
  hours_change: 'Hours', clock_fix: 'Clock fix',
};
const STATUS_TONE = {
  pending: 'attnToneWarn', approved: 'attnToneGray',
  declined: 'attnToneDue', withdrawn: 'attnToneGray',
};
const REQ_TABS = [
  { id: 'pending', label: 'Waiting' },
  { id: '', label: 'All' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
];

const cellKey = (idStaff, day) => `${idStaff}|${day}`;

// The day grid covers 6 AM to 10 PM, which is where this shop's work falls.
// Anything outside is clamped rather than dropped.
const GRID_FROM = 6 * 60;
const GRID_TO = 22 * 60;
const GRID_SPAN = GRID_TO - GRID_FROM;
const pct = (mins) => `${Math.min(100, Math.max(0, ((mins - GRID_FROM) / GRID_SPAN) * 100))}%`;
const span = (from, to) => `calc(${pct(to)} - ${pct(from)})`;
const minsIntoDay = (v) => {
  const dt = new Date(v);
  return dt.getHours() * 60 + dt.getMinutes();
};
const dLong = (key) => utcDay(key).toLocaleDateString('en-US',
  { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });

export function SchedulePage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const reqRef = useRef(null);

  const today = localDay(new Date());
  const [week, setWeek] = useState(weekStartOf(today));
  const [city, setCity] = useState('');
  const [spot, setSpot] = useState('');
  const [view, setView] = useState('week');
  const [day, setDay] = useState(localDay(new Date()));
  const [selection, setSelection] = useState([]);
  const [custom, setCustom] = useState({ from: '10:00', to: '18:00' });
  // The "add another time" form, which is only meaningful on a single cell.
  const [extra, setExtra] = useState({ from: '08:00', to: '12:00' });
  // Which garage a shift lands at. Empty means "wherever the row says", which
  // is right most of the time: a cell is picked from a garage's own block. It
  // is set explicitly when somebody is being sent to cover elsewhere.
  const [applyLoc, setApplyLoc] = useState('');
  // The preset being dragged, and the cell it is currently over. Held in state
  // rather than dataTransfer because Firefox will not let you read the payload
  // during dragover, and the cell needs to know what it would receive.
  const [dragging, setDragging] = useState(null);
  const [dropOn, setDropOn] = useState(null);
  const [reqTab, setReqTab] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const weekEnd = addDays(week, 6);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);

  const { data: staffData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    enabled: isAdmin,
    staleTime: Infinity,
  });
  const roster = staffData?.staffs || [];

  const { data: schedData, isLoading } = useQuery({
    queryKey: ['schedule-week', week],
    queryFn: () => ScheduleService.list({ start: addDays(week, -1), end: addDays(weekEnd, 1) }),
    enabled: isAdmin,
    placeholderData: (prev) => prev,
  });

  const { data: reqData } = useQuery({
    queryKey: ['crew-requests', reqTab],
    queryFn: () => ShiftRequestsService.list(reqTab ? { status: reqTab } : {}),
    enabled: isAdmin,
    placeholderData: (prev) => prev,
  });
  const { data: pendingData } = useQuery({
    queryKey: ['crew-requests', 'pending'],
    queryFn: () => ShiftRequestsService.list({ status: 'pending' }),
    enabled: isAdmin,
  });

  // Approved time off, fetched for the grid rather than taken from whatever
  // the table below happens to be showing. The table defaults to Waiting, so
  // reading its rows meant an approved day off never shaded its cell — and
  // changing tabs silently changed the grid.
  const { data: approvedData } = useQuery({
    queryKey: ['crew-requests', 'approved'],
    queryFn: () => ShiftRequestsService.list({ status: 'approved' }),
    enabled: isAdmin,
  });

  const posted = schedData?.posted || {};
  const cars = schedData?.cars || {};
  const isPosted = !!posted[week];
  // Posted means locked here. The mockup carries the two as separate flags so a
  // posted week can be opened for edits without the crew losing sight of it;
  // this has one flag, so unposting is how you reopen it.
  const locked = isPosted;
  const requests = reqData?.requests || [];
  const pending = pendingData?.requests || [];

  // Which garages are on screen. The city filter narrows them; "All" shows the
  // lot, which is how a manager covering both coasts reads the week.
  // Which garages are on screen. City narrows to a coast; the chips under it
  // narrow to one garage, which is how you plan a single site without the
  // other three in the way.
  const inCity = useMemo(
    () => Object.entries(LOCATION_META)
      .filter(([, meta]) => !city || meta.city === city)
      .map(([id, meta]) => ({ id, ...meta })),
    [city]);

  const locations = useMemo(
    () => (spot ? inCity.filter(l => l.id === spot) : inCity),
    [inCity, spot]);

  // Crew belong to garages, and some belong to more than one. A person shared
  // between two shows up under both, which is the point: you schedule them
  // where you need them, and the hours column counts the whole week either way.
  const crewAt = useMemo(() => {
    const m = {};
    for (const loc of locations) {
      m[loc.id] = roster.filter(r => (r.locationIds || []).includes(loc.id));
    }
    return m;
  }, [locations, roster]);

  const grid = useMemo(() => {
    const m = {};
    for (const x of (schedData?.schedules || [])) {
      const day = localDay(x.startAt);
      if (day < week || day > weekEnd) continue;
      (m[cellKey(x.idStaff, day)] ||= []).push({ ...x, day });
    }
    for (const list of Object.values(m)) {
      list.sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
    }
    return m;
  }, [schedData, week, weekEnd]);

  // Days a person has already been granted off, or asked about. The panel
  // skips these rather than painting over somebody's approved holiday.
  const blocked = useMemo(() => {
    const m = {};
    for (const r of (pendingData?.requests || []).concat(approvedData?.requests || [])) {
      if (r.kind !== 'time_off' && r.kind !== 'day_unavailable') continue;
      if (r.status !== 'approved' && r.status !== 'pending') continue;
      if (!r.fromDate) continue;
      const first = iso(r.fromDate);
      const last = iso(r.toDate || r.fromDate);
      for (let d = utcDay(first); iso(d) <= last; d.setUTCDate(d.getUTCDate() + 1)) {
        m[cellKey(r.idStaff, iso(d))] = r.status === 'pending'
          ? { kind: 'Requested', note: r.reason || '', state: 'waiting' }
          : {
              kind: r.kind === 'time_off' ? 'Time off' : 'Unavail.',
              note: r.reason || '',
              state: r.kind === 'time_off' ? 'off' : 'unavail',
            };
      }
    }
    return m;
  }, [pendingData, approvedData]);

  const stats = useMemo(() => {
    const shown = new Set(locations.map(l => l.id));
    const shifts = Object.values(grid).flat().filter(x => shown.has(x.location));
    const minutes = shifts.reduce((n, x) => n + (x.minutes || 0), 0);
    const eligible = new Set(locations.flatMap(l => (crewAt[l.id] || []).map(m => m.idStaff)));
    const working = new Set(shifts.map(x => x.idStaff).filter(id => eligible.has(id)));
    let bare = 0;
    for (const loc of locations) {
      for (const day of weekDays) {
        const booked = cars[`${loc.id}|${day}`] || 0;
        if (!booked) continue;
        const on = (crewAt[loc.id] || []).filter(m => (grid[cellKey(m.idStaff, day)] || []).length).length;
        if (!on || booked / on > 4.5) bare += 1;
      }
    }
    return [
      { k: 'Scheduled', v: dur(minutes) || '0h',
        note: `${shifts.length} shift${shifts.length === 1 ? '' : 's'}` },
      { k: 'Crew working', v: String(working.size),
        note: `of ${eligible.size} who can work here` },
      { k: 'Short-staffed', v: String(bare), tone: bare ? 'attnToneDue' : 'attnToneGray',
        note: bare ? 'garage-days over 4.5 cars per crew' : 'every day is covered' },
      { k: 'Requests', v: String(pending.length), tone: pending.length ? 'attnToneWarn' : '',
        note: pending.length ? 'waiting for you' : 'nothing waiting' },
    ];
  }, [grid, locations, crewAt, weekDays, pending, cars]);

  const minutesFor = (idStaff) => weekDays.reduce((n, day) =>
    n + (grid[cellKey(idStaff, day)] || []).reduce((a, x) => a + (x.minutes || 0), 0), 0);

  // Stepping a day can walk out of the week on screen, which has to follow or
  // the grid would be showing one week and the header another.
  const stepDay = (n) => {
    const next = addDays(day, n);
    setDay(next);
    setSelection([]);
    if (weekStartOf(next) !== week) setWeek(weekStartOf(next));
  };

  const pickCity = (next) => {
    setCity(next);
    setSpot('');
    setSelection([]);
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['schedule-week'] });
    queryClient.invalidateQueries({ queryKey: ['crew-requests'] });
    queryClient.invalidateQueries({ queryKey: ['my-shifts'] });
    queryClient.invalidateQueries({ queryKey: ['shift-upcoming'] });
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const out = await fn();
      if (okMsg) setNotice(typeof okMsg === 'function' ? okMsg(out) : okMsg);
      refresh();
      return out;
    } catch (err) {
      setError(err?.error || err?.message || 'That did not go through.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const toggleCell = (idStaff, day, locId) => {
    if (locked) return;
    setApplyLoc('');
    const key = cellKey(idStaff, day);
    setSelection(prev => (prev.some(c => c.key === key)
      ? prev.filter(c => c.key !== key)
      : prev.concat([{ key, idStaff, day, location: locId }])));
  };

  const applyWindow = (fromMins, toMins) => {
    const usable = selection.filter(c => !blocked[c.key]);
    if (!usable.length) {
      setError('That day is time off or already spoken for.');
      return;
    }
    return run(
      () => ScheduleService.apply(usable.map(c => ({
        idStaff: c.idStaff,
        location: targetLoc,
        startAt: new Date(`${c.day}T${hhmm(fromMins)}`).toISOString(),
        endAt: new Date(`${c.day}T${hhmm(toMins)}`).toISOString(),
      }))),
      () => 'Shift applied.')
      .then((out) => { if (out) setSelection([]); });
  };

  const dropShift = (member, day, locId) => {
    const tpl = dragging;
    setDragging(null);
    setDropOn(null);
    if (!tpl || locked) return;

    const key = cellKey(member.idStaff, day);
    if (blocked[key]) {
      setError('That day is time off or already spoken for.');
      return;
    }

    if (!(member.locationIds || []).includes(locId)) {
      setError(`${fullName(member)} does not work at ${LOCATION_META[locId]?.name || 'that garage'}.`);
      return;
    }
    return run(
      () => ScheduleService.apply([{
        idStaff: member.idStaff,
        location: locId,
        startAt: new Date(`${day}T${hhmm(tpl.from)}`).toISOString(),
        endAt: new Date(`${day}T${hhmm(tpl.to)}`).toISOString(),
      }]),
      `${tpl.label} applied to ${fullName(member)} on ${dowOf(day)}.`);
  };

  const removeSelected = () => {
    const ids = selection.flatMap(c => (grid[c.key] || []).map(x => x.idSchedule)).filter(Boolean);
    if (!ids.length) { setSelection([]); return; }
    return run(() => ScheduleService.removeMany(ids),
      (r) => `Removed ${r.removed} shift${r.removed === 1 ? '' : 's'}.`)
      .then((out) => { if (out) setSelection([]); });
  };

  const only = selection.length === 1 ? selection[0] : null;
  const onlyMember = only
    ? roster.find(r => String(r.idStaff) === String(only.idStaff))
    : null;

  // Everybody in the selection, and the garages all of them work at. The
  // roster's own location list is the authority. An intersection across one
  // person is simply that person's list, so this covers both cases.
  const chosenCrew = useMemo(
    () => [...new Set(selection.map(c => String(c.idStaff)))]
      .map(id => roster.find(r => String(r.idStaff) === id))
      .filter(Boolean),
    [selection, roster]);

  const openTo = useMemo(() => {
    if (!chosenCrew.length) return [];
    return inCity.filter(l => chosenCrew.every(m => (m.locationIds || []).includes(l.id)));
  }, [chosenCrew, inCity]);

  // Where an applied shift lands. An explicit choice wins; a single shared
  // garage needs no choosing; none shared means nothing can be applied.
  const targetLoc = (applyLoc && openTo.some(l => l.id === applyLoc))
    ? applyLoc
    : (openTo.length === 1 ? openTo[0].id : '');
  const canApply = !locked && !!targetLoc;
  const onlyShifts = only ? (grid[only.key] || []) : [];
  const onlyMinutes = onlyShifts.reduce((n, x) => n + (x.minutes || 0), 0);
  const onlyName = onlyMember || null;

  const addTime = () => run(
    () => ScheduleService.create({
      idStaff: only.idStaff,
      location: targetLoc || only.location,
      startAt: new Date(`${only.day}T${extra.from}`).toISOString(),
      endAt: new Date(`${only.day}T${extra.to}`).toISOString(),
    }),
    'Time added.');

  const dropTime = (idSchedule) => run(
    () => ScheduleService.remove(idSchedule), 'Time removed.');

  const selLabel = only
    ? `${onlyMember ? fullName(onlyMember) : 'Crew'} · ${dowOf(only.day)} ${dShort(only.day)}`
    : `${chosenCrew.map(m => (m.given_name || fullName(m))).join(', ')}`
      + ` · ${[...new Set(selection.map(c => c.day))].length} day`
      + `${[...new Set(selection.map(c => c.day))].length === 1 ? '' : 's'}`;

  if (!isAdmin) {
    return (
      <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
        <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />
        <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
          <div className={s.attnEmpty}>
            Planning the schedule is an admin job. Your own shifts are on the My Shifts page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.shiftPage}>
        <div className={s.schedHead}>
          <div>
            <div className={s.dayEyebrowRow}>
              <span className={s.shiftEyebrow}>Schedule</span>
              <span className={`${s.schedTag} ${isPosted ? s.attnToneGray : s.attnToneWarn}`}>
                {isPosted ? 'posted · locked' : 'draft · crew cannot see it'}
              </span>
            </div>
            <h1 className={`${s.shiftToday} ${s.schedTitle}`}>{dShort(week)} – {dShort(weekEnd)}, {weekEnd.slice(0, 4)}</h1>
          </div>

          <div className={s.schedActions}>
            <button className={s.dayAction} disabled={busy}
                    onClick={() => run(() => ScheduleService.copyWeek(week, addDays(week, 7)),
                      (r) => `Copied ${r.copied} shift${r.copied === 1 ? '' : 's'} into next week`
                        + (r.skipped.length ? `, ${r.skipped.length} already there.` : '.'))}>
              Copy to next week
            </button>
            <button className={isPosted ? s.dayAction : s.schedPost} disabled={busy}
                    onClick={() => { setSelection([]); return run(
                      () => ScheduleService.postWeek(week, !isPosted),
                      isPosted ? 'Unlocked. The crew stop seeing it until you post again.'
                               : 'Posted to the crew and locked.'); }}>
              {isPosted ? 'Unlock to adjust' : 'Post to crew & lock'}
            </button>
          </div>
        </div>

        <div className={s.schedNav}>
          <button className={s.calNavBtn} aria-label="Previous"
                  onClick={() => (view === 'day' ? stepDay(-1) : setWeek(addDays(week, -7)))}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="square"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button className={s.calNavBtn} aria-label="Next"
                  onClick={() => (view === 'day' ? stepDay(1) : setWeek(addDays(week, 7)))}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="square"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          {week !== weekStartOf(today) && (
            <button className={s.calToday} onClick={() => setWeek(weekStartOf(today))}>This week</button>
          )}

          <div className={s.calSeg}>
            <button onClick={() => pickCity('')}
                    className={`${s.calSegBtn} ${!city ? s.calSegOn : ''}`}>All</button>
            {CITIES.map(c => (
              <button key={c} onClick={() => pickCity(c)}
                      className={`${s.calSegBtn} ${city === c ? s.calSegOn : ''}`}>{c}</button>
            ))}
          </div>

          {city && inCity.length > 1 && (
            <div className={s.schedSpots}>
              <button onClick={() => { setSpot(''); setSelection([]); }}
                      className={`${s.schedSpot} ${!spot ? s.schedSpotOn : ''}`}>
                {inCity.length === 2 ? 'Both garages' : 'All garages'}
              </button>
              {inCity.map(l => (
                <button key={l.id} onClick={() => { setSpot(l.id); setSelection([]); }}
                        className={`${s.schedSpot} ${spot === l.id ? s.schedSpotOn : ''}`}>
                  {l.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div className={s.attnError}>{error}</div>}
        {notice && <div className={s.schedNotice}>{notice}</div>}

        {schedData && schedData.ready === false && (
          <div className={s.attnError}>
            The shift tables are not on this database yet, so the week below is
            empty because there is nowhere to read it from — not because nothing
            is planned. Run <code>db_scripts/migrate.js</code> against it.
          </div>
        )}

        <div className={s.schedStats}>
          {stats.map(x => (
            <div key={x.k} className={s.schedStat}>
              <div className={s.schedStatLabel}>{x.k}</div>
              <div className={`${s.schedStatValue} ${x.tone ? s[x.tone] : ''}`}>{x.v}</div>
              <div className={s.schedStatNote}>{x.note}</div>
            </div>
          ))}
        </div>

        {pending.length > 0 && (
          <div className={s.schedBanner}>
            <span className={s.schedBannerDot} />
            <div className={s.schedBannerBody}>
              <div className={s.schedBannerTitle}>
                {pending.length} request{pending.length === 1 ? '' : 's'} waiting for you
              </div>
              <div className={s.schedBannerSub}>
                {pending.slice(0, 2).map(r => [
                  r.staffName, (REQ_LABEL[r.kind] || r.kind).toLowerCase(),
                  r.fromDate ? dShort(iso(r.fromDate)) : null,
                ].filter(Boolean).join(' · ')).join('   ·   ')}
                {pending.length > 2 ? `   and ${pending.length - 2} more` : ''}
              </div>
            </div>
            <button className={s.dayAction}
                    onClick={() => { setReqTab('pending'); reqRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>
              Review requests
            </button>
          </div>
        )}

        <div className={s.schedLayout}>
          {/* Grid and its legend are one column; the panel is the other. */}
          <div className={s.schedMain}>
          {/* The card header sits outside the scroller: inside it, the
              title slid out of view the moment you scrolled across the
              week. Only the grid itself moves. */}
          <div className={s.schedWrapHead}>
            <span className={s.schedWrapTitle}>
              {view === 'day' ? dLong(day) : 'Shifts this week'}
            </span>
            <div className={s.calSeg} style={{ marginLeft: 'auto' }}>
              {['week', 'day'].map(v => (
                <button key={v} onClick={() => { setView(v); setSelection([]); }}
                        className={`${s.calSegBtn} ${view === v ? s.calSegOn : ''}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className={s.schedWrap}>
            {view === 'day' && locations.map(loc => {
              const crew = crewAt[loc.id] || [];
              const booked = cars[`${loc.id}|${day}`] || 0;
              const on = crew.filter(m => (grid[cellKey(m.idStaff, day)] || []).length).length;
              const nowMins = minsIntoDay(new Date());
              return (
                <div key={loc.id} className={s.schedDayBlock}>
                  <div className={s.schedGroup}>
                    <span className={s.schedGroupDot} style={{ background: loc.color }} />
                    <span className={s.schedGroupName}>{loc.name}</span>
                    <span className={s.schedGroupMeta}>
                      {on} crew &middot; {booked} car{booked === 1 ? '' : 's'}
                    </span>
                  </div>

                  {!crew.length && (
                    <div className={s.railEmpty}>Nobody on the roster works here yet.</div>
                  )}

                  {crew.length > 0 && (
                    <div className={s.schedDayGrid}
                         style={{ gridTemplateColumns: `52px repeat(${crew.length}, minmax(120px, 1fr))` }}>
                      <div className={s.schedDayGutter}>
                        {Array.from({ length: GRID_SPAN / 60 + 1 }, (_, i) => {
                          const h = GRID_FROM / 60 + i;
                          return (
                            <div key={h} className={s.calHour}>
                              {new Date(Date.UTC(2020, 0, 1, h)).toLocaleTimeString('en-US',
                                { hour: 'numeric', timeZone: 'UTC' })}
                            </div>
                          );
                        })}
                      </div>

                      {crew.map(member => {
                        const key = cellKey(member.idStaff, day);
                        const shifts = grid[key] || [];
                        const off = blocked[key];
                        const mins = shifts.reduce((n, x) => n + (x.minutes || 0), 0);
                        const picked = selection.some(c => c.key === key);
                        return (
                          <button key={member.idStaff} type="button" disabled={locked}
                                  className={`${s.schedDayCol} ${picked ? s.schedCellOn : ''} ${
                                    dropOn === key ? s.schedCellDrop : ''}`}
                                  onDragOver={(e) => {
                                    if (!dragging || locked) return;
                                    e.preventDefault();
                                    if (dropOn !== key) setDropOn(key);
                                  }}
                                  onDragLeave={() => setDropOn(d => (d === key ? null : d))}
                                  onDrop={(e) => { e.preventDefault(); dropShift(member, day, loc.id); }}
                                  onClick={() => toggleCell(member.idStaff, day, loc.id)}>
                            <span className={s.schedDayHeadCell}>
                              <span className={s.schedDayWho} title={fullName(member)}>
                                {(member.given_name || fullName(member)).toUpperCase()}
                              </span>
                              <span className={s.schedDayHours}>{dur(mins) || '—'}</span>
                            </span>

                            <span className={`${s.schedDayBody} ${off ? s[
                              `schedCell${off.state === 'waiting' ? 'Waiting'
                                : off.state === 'off' ? 'Off' : 'Unavail'}`] : ''}`}>
                              {off && !shifts.length && (
                                <span className={s.schedDayOff}>{off.kind}</span>
                              )}
                              {shifts.map(x => (
                                <span key={x.idSchedule}
                                      className={`${s.schedDayShift} ${
                                        x.location !== loc.id ? s.schedChipElsewhere : ''}`}
                                      style={{ top: pct(minsIntoDay(x.startAt)),
                                               height: span(minsIntoDay(x.startAt), minsIntoDay(x.endAt)) }}>
                                  <span className={s.schedChipTime}>
                                    {tm(x.startAt)} &ndash; {tm(x.endAt)}
                                  </span>
                                  <span className={s.schedChipSub}>{dur(x.minutes)}</span>
                                </span>
                              ))}
                              {day === today && (
                                <span className={s.calNow} style={{ top: pct(nowMins) }} />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {view === 'week' && (
            <div className={s.schedMin}>
              <div className={`${s.schedRow} ${s.schedHeadRow}`}>
                <div className={`${s.schedCrewCell} ${s.schedHeadCell}`}>Crew</div>
                {weekDays.map(day => (
                  <div key={day} className={`${s.schedDayHead} ${day === today ? s.schedDayToday : ''}`}>
                    <span className={s.calColDow}>{dowOf(day)}</span>
                    <span className={s.schedDayNum}>{Number(day.slice(8, 10))}</span>
                  </div>
                ))}
                <div className={`${s.schedHoursCell} ${s.schedHeadCell}`}>Hours</div>
              </div>

              {locations.map(loc => {
                const crew = crewAt[loc.id] || [];
                const shared = crew.filter(r => (r.locationIds || []).length > 1).length;
                return (
                  <div key={loc.id}>
                    <div className={s.schedGroup}>
                      <span className={s.schedGroupDot} style={{ background: loc.color }} />
                      <span className={s.schedGroupName}>{loc.name} {loc.city}</span>
                      <span className={s.schedGroupMeta}>
                        {loc.city} · {crew.length - shared} crew{shared ? ` + ${shared} shared` : ''}
                      </span>
                    </div>

                    {crew.map(member => (
                      <div key={`${loc.id}-${member.idStaff}`} className={s.schedRow}>
                        <div className={s.schedCrewCell}>
                          <span className={s.schedAvatar}>{initials(fullName(member))}</span>
                          <span className={s.schedCrewWho}>
                            <span className={s.schedCrewName} title={fullName(member)}>
                              {fullName(member)}
                            </span>
                            <span className={s.schedCrewRole}>
                              {(member.locationIds || []).length > 1
                                ? `Shared`
                                : 'Detailer'}
                            </span>
                          </span>
                        </div>

                        {weekDays.map(day => {
                          const key = cellKey(member.idStaff, day);
                          const shifts = grid[key] || [];
                          const off = blocked[key];
                          const on = selection.some(c => c.key === key);
                          return (
                            <button key={day} type="button" disabled={locked}
                                    className={[s.schedCell, on && s.schedCellOn,
                                      off && s[`schedCell${off.state === 'waiting' ? 'Waiting'
                                        : off.state === 'off' ? 'Off' : 'Unavail'}`],
                                      dropOn === key && s.schedCellDrop,
                                      locked && s.schedCellLocked].filter(Boolean).join(' ')}
                                    onDragOver={(e) => {
                                      if (!dragging || locked) return;
                                      e.preventDefault();
                                      if (dropOn !== key) setDropOn(key);
                                    }}
                                    onDragLeave={() => setDropOn(d => (d === key ? null : d))}
                                    onDrop={(e) => { e.preventDefault(); dropShift(member, day, loc.id); }}
                                    onClick={() => toggleCell(member.idStaff, day, loc.id)}>
                              {off && !shifts.length && (
                                <span className={`${s.schedOffChip} ${on ? s.schedBoxOn : ''}`}>
                                  <span className={s.schedOffKind}>{off.kind}</span>
                                  {off.note && <span className={s.schedChipSub}>{off.note}</span>}
                                </span>
                              )}
                              {shifts.map(x => (
                                <span key={x.idSchedule}
                                      className={[s.schedChip,
                                        x.location !== loc.id && s.schedChipElsewhere,
                                        on && s.schedBoxOn].filter(Boolean).join(' ')}>
                                  <span className={s.schedChipTime}>{tm(x.startAt)}–{tm(x.endAt)}</span>
                                  <span className={s.schedChipSub}>
                                    {x.locationName}{x.bay ? ` · ${x.bay}` : ''}
                                  </span>
                                </span>
                              ))}
                              {/* An empty cell still has to show it is chosen. */}
                              {on && !shifts.length && !off && <span className={s.schedBoxEmpty} />}
                            </button>
                          );
                        })}

                        <div className={s.schedHoursCell}>{dur(minutesFor(member.idStaff)) || '—'}</div>
                      </div>
                    ))}

                    {!crew.length && (
                      <div className={s.schedRow}>
                        <div className={`${s.schedCrewCell} ${s.schedGroupEmpty}`}>
                          Nobody on the roster works here yet.
                        </div>
                        {weekDays.map(day => <div key={day} className={s.schedCellStatic} />)}
                        <div className={s.schedHoursCell} />
                      </div>
                    )}

                    <div className={`${s.schedRow} ${s.schedCoverRow}`}>
                      <div className={`${s.schedCrewCell} ${s.schedCoverLabel}`}>Crew / cars booked</div>
                      {weekDays.map(day => {
                        const on = crew.filter(m => (grid[cellKey(m.idStaff, day)] || []).length).length;
                        const booked = cars[`${loc.id}|${day}`] || 0;
                        const thin = booked > 0 && (!on || booked / on > 4.5);
                        return (
                          <div key={day} className={`${s.schedCover} ${thin ? s.schedCoverNone : ''}`}>
                            {on} crew · {booked} car{booked === 1 ? '' : 's'}
                          </div>
                        );
                      })}
                      <div className={s.schedHoursCell} />
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>

            <div className={s.schedLegend}>
              <span className={s.schedLegendHint}>
                {locked
                  ? 'This week is locked. Unlock to adjust shifts.'
                  : 'Click a cell, or several across the week, then apply a shift from the panel.'}
              </span>
              <span><i className={s.swatchUnavail} />Unavailable</span>
              <span><i className={s.swatchOff} />Time off</span>
              <span><i className={s.swatchWaiting} />Request waiting</span>
              <span><i className={s.swatchElsewhere} />Covering elsewhere · select it to add here too</span>
            </div>
          </div>

          {/* The selection panel. Beside the grid rather than over it, so the
              week being painted stays readable while it is used. */}
          <aside className={s.schedPanel}>
          {!selection.length && (
            <div className={s.schedPanelIdle}>
              <div className={s.schedPanelHead}>
                <div>
                  <div className={s.shiftLabel}>Shifts</div>
                  <div className={s.dayTitle}>Drag onto the schedule</div>
                  <div className={s.daySub}>
                    Drop a shift on any cell. Or click a cell to edit it here.
                  </div>
                </div>
              </div>

              <div className={s.schedPanelBody}>
                <div className={s.shiftLabel}>Presets</div>
                <div className={s.schedPresets}>
                  {PRESETS.map(p => (
                    <div key={p.id}
                         draggable={!locked}
                         onDragStart={() => setDragging({ ...p })}
                         onDragEnd={() => { setDragging(null); setDropOn(null); }}
                         className={`${s.schedPreset} ${s.schedDraggable} ${
                           dragging?.id === p.id ? s.schedPresetLifted : ''}`}>
                      <span className={s.schedGrip} aria-hidden="true">⠿</span>
                      <span className={s.schedPresetBody}>
                        <span className={s.schedPresetName}>{p.label}</span>
                        <span className={s.schedChipSub}>{dur(p.to - p.from)}</span>
                      </span>
                      <span className={s.schedPresetTime}>{label12(p.from)}–{label12(p.to)}</span>
                    </div>
                  ))}
                </div>

                <div className={s.shiftLabel}>Custom hours</div>
                <div className={s.schedFieldRow}>
                  <input className={s.schedInput} type="time" value={custom.from}
                         onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
                  <input className={s.schedInput} type="time" value={custom.to}
                         onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
                </div>
                <div draggable={!locked}
                     onDragStart={() => setDragging({
                       id: 'custom', label: 'Custom',
                       from: minsOf(custom.from), to: minsOf(custom.to),
                     })}
                     onDragEnd={() => { setDragging(null); setDropOn(null); }}
                     className={`${s.schedPreset} ${s.schedDraggable} ${
                       dragging?.id === 'custom' ? s.schedPresetLifted : ''}`}>
                  <span className={s.schedGrip} aria-hidden="true">⠿</span>
                  <span className={s.schedPresetBody}>
                    <span className={s.schedPresetName}>Custom</span>
                    <span className={s.schedChipSub}>
                      {dur(Math.max(0, minsOf(custom.to) - minsOf(custom.from)))}
                    </span>
                  </span>
                  <span className={s.schedPresetTime}>
                    {label12(minsOf(custom.from))}–{label12(minsOf(custom.to))}
                  </span>
                </div>

                <div className={s.schedPanelNote}>
                  {locked
                    ? 'This week is locked. Unlock it to drag shifts in.'
                    : 'Click a cell to edit it here, or drag a shift straight onto it.'}
                </div>
              </div>
            </div>
          )}

          {selection.length > 0 && (
            <div className={s.schedPanelCard}>
              <div className={s.schedPanelHead}>
                <div>
                  <div className={s.shiftLabel}>Selected</div>
                  <div className={s.dayTitle}>
                    {selection.length} cell{selection.length === 1 ? '' : 's'} selected
                  </div>
                  <div className={s.daySub}>{selLabel}</div>
                </div>
                <button className={s.schedClose}
                        onClick={() => { setSelection([]); setApplyLoc(''); }}
                        aria-label="Clear selection">×</button>
              </div>

              <div className={s.schedPanelBody}>
                {/* Where these shifts land. First, because it changes what
                    every button below does, and a shift applied to the wrong
                    garage is a person sent to the wrong building. */}
                <div className={s.shiftLabel}>Location</div>
                {openTo.length > 0 && (
                  <div className={s.schedSpots}>
                    {openTo.map(l => (
                      <button key={l.id}
                              className={`${s.schedSpot} ${targetLoc === l.id ? s.schedSpotOn : ''}`}
                              onClick={() => setApplyLoc(l.id)}>
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
                {!openTo.length && (
                  <div className={s.schedWarn}>
                    {chosenCrew.map(m => (m.given_name || fullName(m))).join(', ')} do not share a
                    garage, so one shift cannot be applied to all of them. Narrow the selection.
                  </div>
                )}
                {openTo.length === 1 && (
                  <div className={s.schedPanelNote} style={{ border: 'none', paddingTop: 0 }}>
                    {selection.length === 1
                      ? `${(onlyName && (onlyName.given_name || fullName(onlyName))) || 'This person'} only works at ${openTo[0].name}.`
                      : `${openTo[0].name} is the only garage they all work at.`}
                  </div>
                )}

                  {/* One cell, so its own times can be listed and changed
                    individually. Several cells share only the bulk actions
                    below, since "this day" means nothing across seven of them. */}
                {only && (
                  <>
                    <div className={s.shiftLabel}>
                      Times for {(onlyName && (onlyName.given_name || fullName(onlyName))) || 'this person'} this day
                    </div>
                    {!onlyShifts.length && (
                      <div className={s.schedPanelNote} style={{ border: 'none', paddingTop: 0 }}>
                        Nothing planned yet — pick a shift below.
                      </div>
                    )}
                    {onlyShifts.map(x => (
                      <div key={x.idSchedule} className={s.schedTime}>
                        <span className={s.schedTimeBody}>
                          <span className={s.schedTimeWhen}>{tm(x.startAt)} – {tm(x.endAt)}</span>
                          <span className={s.schedChipSub}>{x.locationName}</span>
                        </span>
                        <button className={s.schedTimeDrop} disabled={busy || locked}
                                title="Remove this time"
                                onClick={() => dropTime(x.idSchedule)}>×</button>
                      </div>
                    ))}
                    {onlyShifts.length > 0 && (
                      <div className={s.schedTimeTotal}>Total {dur(onlyMinutes)}</div>
                    )}

                    {onlyShifts.length > 0 && (
                    <div className={s.schedAdd}>
                      <div className={s.schedAddTitle}>Add another time</div>
                      <div className={s.schedFieldRow}>
                        <input className={s.schedInput} type="time" value={extra.from} disabled={busy}
                               onChange={(e) => setExtra({ ...extra, from: e.target.value })} />
                        <input className={s.schedInput} type="time" value={extra.to} disabled={busy}
                               onChange={(e) => setExtra({ ...extra, to: e.target.value })} />
                      </div>
                      <button className={s.schedPost} disabled={busy || !canApply} onClick={addTime}>
                        {busy ? 'Adding…' : `Add time at ${
                          LOCATION_META[applyLoc || only.location]?.name || 'this garage'}`}
                      </button>
                    </div>
                    )}
                  </>
                )}

              <div className={s.shiftLabel}>Apply a shift</div>
                <div className={s.schedPresets}>
                  {PRESETS.map(p => (
                    <button key={p.id} className={s.schedPreset} disabled={busy || !canApply}
                            onClick={() => applyWindow(p.from, p.to)}>
                      <span className={s.schedPresetName}>{p.label}</span>
                      <span className={s.schedPresetTime}>{label12(p.from)}–{label12(p.to)}</span>
                    </button>
                  ))}
                </div>

                <div className={s.shiftLabel}>Custom hours</div>
                <div className={s.schedFieldRow}>
                  <input className={s.schedInput} type="time" value={custom.from} disabled={busy}
                         onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
                  <input className={s.schedInput} type="time" value={custom.to} disabled={busy}
                         onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
                </div>
                <button className={s.schedPost} disabled={busy || !canApply}
                        onClick={() => applyWindow(minsOf(custom.from), minsOf(custom.to))}>
                  {busy ? 'Applying…' : 'Apply custom hours'}
                </button>

                <div className={s.schedPanelActions}>
                  <button className={s.dayAction} disabled={busy} onClick={removeSelected}>Remove shifts</button>
                  <button className={s.dayAction}
                          onClick={() => { setSelection([]); setApplyLoc(''); }}>Done</button>
                </div>

                <div className={s.schedPanelNote}>
                  A day the crew member is unavailable or on time off cannot be
                  scheduled, and the garages above are the only ones they work at.
                </div>
              </div>
            </div>
          )}
          </aside>
        </div>

        {isLoading && <div className={s.ledgerIntro}>Loading…</div>}

        {/* ── requests, which used to be a page of their own ─────────────── */}
        <div className={s.schedReqHead} ref={reqRef}>
          <h2 className={s.schedReqTitle}>Requests from crew</h2>
          <div className={s.calSeg}>
            {REQ_TABS.map(t => (
              <button key={t.id || 'all'} onClick={() => setReqTab(t.id)}
                      className={`${s.calSegBtn} ${reqTab === t.id ? s.calSegOn : ''}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Crew</th><th>Request</th><th>Dates</th>
                <th>Detail</th><th>Decided by</th><th className={s.num}>Decision</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.idRequest}>
                  <td className={s.schedReqCrew}>
                    <span className={s.schedAvatar}>{initials(r.staffName)}</span>
                    <strong className={s.schedCrewName} title={r.staffName || ''}>
                      {r.staffName || '—'}
                    </strong>
                  </td>
                  <td>
                    <span className={`${s.statusPill} ${s[STATUS_TONE[r.status]] || ''}`}>
                      {REQ_LABEL[r.kind] || r.kind}
                    </span>
                  </td>
                  <td>
                    {r.fromDate
                      ? (iso(r.fromDate) === iso(r.toDate || r.fromDate)
                          ? dShort(iso(r.fromDate))
                          : `${dShort(iso(r.fromDate))} – ${dShort(iso(r.toDate))}`)
                      : [r.proposedClockInAt && `in ${tm(r.proposedClockInAt)}`,
                         r.proposedClockOutAt && `out ${tm(r.proposedClockOutAt)}`]
                          .filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className={s.cellWrap}>
                    {r.reason || r.details || '—'}
                    <span className={s.subCell}>sent {dShort(localDay(r.createAt))}</span>
                  </td>
                  <td>{r.decidedByName || '—'}</td>
                  <td className={s.num}>
                    {r.status === 'pending' ? (
                      <span className={s.schedDecide}>
                        <button className={s.dayAction} disabled={busy}
                                onClick={() => run(() => ShiftRequestsService.decline(r.idRequest), 'Declined.')}>
                          Decline
                        </button>
                        <button className={s.schedPost} disabled={busy}
                                onClick={() => run(() => ShiftRequestsService.approve(r.idRequest), 'Approved.')}>
                          Approve
                        </button>
                      </span>
                    ) : (
                      <span className={`${s.statusPill} ${s[STATUS_TONE[r.status]] || ''}`}>{r.status}</span>
                    )}
                  </td>
                </tr>
              ))}
              {!requests.length && (
                <tr><td colSpan={6} className={s.empty}>
                  {reqTab === 'pending' ? 'Nothing is waiting on a decision.' : 'Nothing here for this filter.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
